import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { materialStatus, MaterialStatus, CourseMaterial } from '../../../../core/interfaces/course-material';

interface CategoryVisual {
  icon: string;
  bg: string;
  color: string;
}

const CATEGORY_VISUALS: Record<string, CategoryVisual> = {
  Pdf:      { icon: 'fa-solid fa-file-pdf',         bg: 'rgba(243,22,78,.10)',   color: 'var(--cred4)'    },
  Video:    { icon: 'fa-solid fa-circle-play',      bg: 'rgba(105,108,255,.10)', color: 'var(--cpurple1)' },
  Image:    { icon: 'fa-solid fa-image',            bg: 'rgba(45,202,114,.10)',  color: 'var(--cgreen5)'  },
  Slide:    { icon: 'fa-solid fa-file-powerpoint',  bg: 'rgba(255,194,51,.14)',  color: 'var(--cyellow4)' },
  Document: { icon: 'fa-solid fa-file-lines',       bg: 'rgba(0,172,255,.10)',   color: 'var(--cblue5)'   },
  Audio:    { icon: 'fa-solid fa-headphones',       bg: 'rgba(236,88,216,.10)',  color: 'var(--cpurple0)' },
  Other:    { icon: 'fa-solid fa-paperclip',        bg: 'rgba(85,85,109,.10)',   color: 'var(--cdark4)'   },
};

@Component({
  selector: 'app-course-material-card',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './course-material-card.component.html',
  styleUrl: './course-material-card.component.css',
})
export class CourseMaterialCardComponent {
  @Input({ required: true }) material!: CourseMaterial;

  @Input() showFavorite = false;
  @Input() isFavorite = false;

  @Output() download = new EventEmitter<CourseMaterial>();
  @Output() openExternal = new EventEmitter<CourseMaterial>();
  @Output() preview = new EventEmitter<CourseMaterial>();
  @Output() favoriteToggled = new EventEmitter<boolean>();

  /** Catégories que la modale d'aperçu sait réellement rendre. */
  private static readonly PREVIEWABLE = ['Pdf', 'Image', 'Video', 'Audio'];

  /**
   * Un lien externe s'ouvre chez son hébergeur, jamais en aperçu : les
   * plateformes vidéo refusent l'intégration en cadre.
   */
  get canPreview(): boolean {
    if (this.isExternal) return false;
    return CourseMaterialCardComponent.PREVIEWABLE.includes(this.material?.category);
  }

  get visual(): CategoryVisual {
    return CATEGORY_VISUALS[this.material?.category] ?? CATEGORY_VISUALS['Other'];
  }

  /** Statut de diffusion — regle partagee avec la bibliotheque. */
  get status(): MaterialStatus {
    return materialStatus(this.material);
  }

  get statusLabelKey(): string {
    return 'LIBRARY.STATUS.' + this.status.toUpperCase();
  }

  get statusIcon(): string {
    switch (this.status) {
      case 'archived':  return 'fa-solid fa-box-archive';
      case 'published': return 'fa-solid fa-circle-check';
      default:          return 'fa-solid fa-clock';
    }
  }

  get isExternal(): boolean {
    return !!this.material?.externalUrl;
  }

  /** Human-readable file size (e.g. "1.4 MB"). Returns empty string when unknown. */
  get sizeLabel(): string {
    const bytes = this.material?.fileSizeBytes;
    if (bytes == null || bytes <= 0) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let v = bytes;
    while (v >= 1024 && i < units.length - 1) {
      v /= 1024;
      i++;
    }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
  }

  onAction(): void {
    if (this.isExternal) this.openExternal.emit(this.material);
    else this.download.emit(this.material);
  }
}
